<template>
    <div id="blacklists-list" class="page-content">
        <!-- Spinner On load -->
        <spinner v-if="blacklists.length == 0 && loading" class="spinner" />

        <!-- If no Folders -->
        <p v-if="blacklists.length == 0 && !loading" class="empty-message">
            {{ $t('blacklist.none') }}
        </p>

        <!-- PULS-945: import file picker (opened from the toolbar menu) and progress -->
        <input ref="importFile" type="file" accept=".csv,text/csv,text/plain" class="import-file" @change="importFileChosen">
        <p v-if="importProgress" class="import-progress mdl-color-text--grey-600">
            {{ importProgress }}
        </p>

        <!-- Conversation items -->
        <transition-group name="flip-list" tag="div">
            <component :is="'BlacklistItem'" v-for="blacklist in blacklists" :key="blacklist.hash" :blacklist-data="blacklist" />
        </transition-group>

        <button v-mdl tag="button" class="create-blacklist mdl-button mdl-js-button mdl-button--fab mdl-js-ripple-effect mdl-button--colored" :style="{ background: $store.state.colors_accent }" @click="createBlacklist">
            <i class="material-icons md-light">add</i>
        </button>
    </div>
</template>

<script>

import Hash from 'object-hash';
import { Api, SessionCache, Util } from '@/utils';
import { findEquivalentEntry, fromCsv, toCsv } from '@/utils/blocklist.js';
import BlacklistItem from './BlacklistItem.vue';
import Spinner from '@/components/Spinner.vue';

// Above this many new entries, confirm first: each one is its own request and push to the phone.
const IMPORT_CONFIRM_THRESHOLD = 100;

export default {
    name: 'Blacklists',

    components: {
        BlacklistItem,
        Spinner
    },

    data () {
        return {
            title: "",
            loading: true,
            blacklists: [],
            importProgress: null,
        };
    },

    mounted () {
        this.$store.state.msgbus.$on('refresh-btn', this.refresh);
        this.$store.state.msgbus.$on('export-blocklist-btn', this.exportBlocklist);
        this.$store.state.msgbus.$on('import-blocklist-btn', this.chooseImportFile);

        this.fetchBlacklists();

        // Construct colors object from saved global theme
        const colors = {
            'default': this.$store.state.theme_global_default,
            'dark': this.$store.state.theme_global_dark,
            'accent': this.$store.state.theme_global_accent,
        };

        // Commit them to current application colors
        this.$store.commit('colors', colors);
    },

    beforeDestroy () {
        this.$store.state.msgbus.$off('refresh-btn', this.refresh);
        this.$store.state.msgbus.$off('export-blocklist-btn', this.exportBlocklist);
        this.$store.state.msgbus.$off('import-blocklist-btn', this.chooseImportFile);
    },

    methods: {

        fetchBlacklists () {
            Api.blacklist.get()
                .then(response => this.processBlacklists(response));
        },

        renderBlacklists (argsBlacklists) {
            const blacklists = [];

            for(let i = 0; i < argsBlacklists.length; i++) {
                const blacklist = argsBlacklists[i];
                blacklist.hash = Hash(blacklist);

                blacklists.push(blacklist);
            }

            this.blacklists = blacklists;
        },

        processBlacklists (response) {
            this.renderBlacklists(response);
            this.loading = false;

            this.$store.commit("loading", false);
            this.$store.commit('title', this.title);
        },

        refresh () {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.fetchBlacklists();
        },

        createBlacklist () {
            this.$router.push({ name: 'create-blacklist'});
        },

        // Built from the decrypted list in the browser; same CSV as Android's export.
        exportBlocklist () {
            const blob = new Blob([toCsv(SessionCache.getBlacklists())], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = 'blocklist.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },

        chooseImportFile () {
            if (this.importProgress) {
                return; // An import is already running
            }

            this.$refs.importFile.value = ''; // Allow choosing the same file again
            this.$refs.importFile.click();
        },

        importFileChosen (event) {
            const file = event.target.files && event.target.files[0];
            if (!file) {
                return;
            }

            file.text()
                .then(csv => this.startImport(fromCsv(csv)))
                .catch(() => Util.snackbar(this.$t('blacklist.import_error')));
        },

        // PULS-1405: skip entries that are already blocked, or repeated within the file.
        startImport (entries) {
            if (entries.length === 0) {
                Util.snackbar(this.$t('blacklist.import_error'));
                return;
            }

            const known = SessionCache.getBlacklists().slice();
            const toAdd = [];
            for (const entry of entries) {
                if (!findEquivalentEntry(known, entry)) {
                    toAdd.push(entry);
                    known.push(entry);
                }
            }

            const skipped = entries.length - toAdd.length;
            if (toAdd.length === 0) {
                Util.snackbar(this.$t('blacklist.imported', { added: 0, skipped, failed: 0 }));
                return;
            }

            if (toAdd.length <= IMPORT_CONFIRM_THRESHOLD) {
                this.runImport(toAdd, skipped);
                return;
            }

            const options = {
                okText: this.$t('dialog.continue'),
                cancelText: this.$t('dialog.cancel'),
                animation: 'fade'
            };
            this.$dialog.confirm(this.$t('blacklist.import_confirm', { count: toAdd.length }), options)
                .then(() => this.runImport(toAdd, skipped))
                .catch(() => { }); // Cancelled
        },

        /**
         * One add request per entry, one after another, like Android's import. The bulk form of
         * blacklists/add only pushes added_blacklist for a single entry, so the phone (which does
         * the blocking) would never hear about a bulk import.
         */
        async runImport (entries, skipped) {
            let added = 0;
            let failed = 0;

            for (let i = 0; i < entries.length; i++) {
                this.importProgress = this.$t('blacklist.importing', { done: i, total: entries.length });

                const entry = entries[i];
                try {
                    if (entry.phone_number) {
                        await Api.blacklist.create.phone(entry.phone_number);
                    } else {
                        await Api.blacklist.create.phrase(entry.phrase);
                    }
                    added++;
                } catch (e) {
                    failed++;
                }
            }

            this.importProgress = null;
            Util.snackbar(this.$t('blacklist.imported', { added, skipped, failed }));

            // Every request has finished, so a refetch now includes them all.
            this.loading = true;
            this.fetchBlacklists();
        }
    },
    watch: {
        '$store.state.blacklists': function () {
            // initial load or refresh is in progress
            if (this.loading) {
                return;
            }
            const blacklists = SessionCache.getBlacklists();
            this.renderBlacklists(blacklists);
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    @import "../../assets/scss/_vars.scss";

    .empty-message {
        color: rgba(0, 0, 0, 0.54);
        margin: 6em auto;
        width: 11.5em;
    }

    .import-file {
        display: none;
    }

    .import-progress {
        text-align: center;
    }

    .create-blacklist {
        position: fixed;
        bottom: 0%;
        right: 0%;
        z-index: 3;
        margin: 24px;
    }

    #blacklists-list {
        width: 100%;

        .spinner {
            margin-top: 100px;
        }
    }

    .flip-list-enter, .flip-list-leave-to	{
        opacity: 0;
    }

    .flip-list-leave-active {
        position: absolute;
    }

    .flip-list-move {
        transition: transform $anim-time;
    }

    body.dark {
        .empty-message {
            color: rgba(255, 255, 255, 0.54);
        }
    }
</style>
